/**
 * IndexRail - 頭文字インデックスのレール
 *
 * 指でなぞると触れている位置のキーが macOS の Dock のように拡大され、
 * 飛び先のキーを大きなバブルで示しながらリアルタイムにジャンプする。
 */

import { PointerEvent as ReactPointerEvent, useCallback, useRef, useState } from 'react'

import { ACCENT_COLOR } from './constants'

/** 拡大の影響が及ぶ範囲(px) */
const MAGNIFY_RANGE = 64
/** 指の真下のキーの拡大率 */
const MAX_SCALE = 2.4
/** 拡大に合わせて左へ逃がす距離(px) */
const MAX_SHIFT = 20

type DragState = { y: number; itemHeight: number }

type IndexRailProps = {
  indexKeys: string[]
  onJump: (indexKey: string) => void
}

export const IndexRail = ({ indexKeys, onJump }: IndexRailProps) => {
  const innerRef = useRef<HTMLDivElement>(null)
  const draggingRef = useRef(false)
  const lastKeyRef = useRef<string | null>(null)
  const [drag, setDrag] = useState<DragState | null>(null)

  const update = useCallback(
    (clientY: number) => {
      const inner = innerRef.current
      if (!inner || indexKeys.length === 0) return

      const rect = inner.getBoundingClientRect()
      const itemHeight = rect.height / indexKeys.length
      if (itemHeight <= 0) return

      const y = Math.min(rect.height, Math.max(0, clientY - rect.top))
      setDrag({ y, itemHeight })

      const index = Math.min(indexKeys.length - 1, Math.floor(y / itemHeight))
      const key = indexKeys.at(index)
      if (!key || key === lastKeyRef.current) return

      lastKeyRef.current = key
      onJump(key)
    },
    [indexKeys, onJump],
  )

  const handlePointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      draggingRef.current = true
      e.currentTarget.setPointerCapture(e.pointerId)
      update(e.clientY)
    },
    [update],
  )

  const handlePointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!draggingRef.current) return
      update(e.clientY)
    },
    [update],
  )

  const handlePointerEnd = useCallback(() => {
    draggingRef.current = false
    lastKeyRef.current = null
    setDrag(null)
  }, [])

  if (indexKeys.length === 0) return null

  const activeIndex = drag ? Math.min(indexKeys.length - 1, Math.floor(drag.y / drag.itemHeight)) : -1
  const activeKey = indexKeys.at(activeIndex)

  return (
    <div
      className="absolute inset-y-0 right-0 z-10 flex w-7 touch-none flex-col items-center justify-center select-none"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
    >
      <div ref={innerRef} className="relative flex w-full flex-col items-center">
        {drag && activeKey && (
          <div
            className="pointer-events-none absolute right-full mr-2 flex size-12 -translate-y-1/2 items-center justify-center rounded-full text-xl font-bold text-white shadow-lg"
            style={{ top: (activeIndex + 0.5) * drag.itemHeight, backgroundColor: ACCENT_COLOR }}
          >
            {activeKey}
          </div>
        )}

        {indexKeys.map((indexKey, i) => {
          const distance = drag ? Math.abs((i + 0.5) * drag.itemHeight - drag.y) : Number.POSITIVE_INFINITY
          const nearness = Math.max(0, 1 - distance / MAGNIFY_RANGE) ** 2
          return (
            <button
              key={indexKey}
              type="button"
              aria-label={`${indexKey} の項目へ移動`}
              onClick={() => onJump(indexKey)}
              className="pointer-events-none block w-full border-none px-1 text-center text-[10px] leading-tight font-semibold"
              style={{
                color: ACCENT_COLOR,
                transform: `translateX(${-MAX_SHIFT * nearness}px) scale(${1 + (MAX_SCALE - 1) * nearness})`,
                transformOrigin: 'right center',
                transition: drag ? 'none' : 'transform 150ms ease-out',
              }}
            >
              {indexKey}
            </button>
          )
        })}
      </div>
    </div>
  )
}
